import { Component, JsonFile } from "projen";
import { TextFile } from "projen/lib/textfile";
import { ClaudeSettingsConfig } from "../agent-config-options";
import {
  AGENT_RULE_SCOPE,
  AgentCommand,
  AgentProcedure,
  AgentRule,
  AgentSkill,
  AgentSubAgent,
  CLAUDE_RULE_TARGET,
  McpServerConfig,
  resolveModelAlias,
} from "../types";

const GENERATED_MARKER =
  "<!-- ~~ Generated by @codedrifters/configulator. Edits welcome — please contribute improvements back. ~~ -->";

/**
 * Renders agent configuration files for Claude Code.
 *
 * Output files:
 * - CLAUDE.md — always-active rules targeted to CLAUDE_MD
 * - `.claude/rules/{name}.md` — scoped rules with optional paths frontmatter
 * - .claude/settings.json — permissions, hooks, MCP servers, sandbox
 * - `.claude/skills/{name}/SKILL.md` — skill definitions
 * - `.claude/agents/{name}.md` — sub-agent definitions
 * - `.claude/procedures/{name}` — executable procedures (shell scripts)
 * - `.claude/commands/{name}.md` — user-invokable slash commands
 */
export class ClaudeRenderer {
  /**
   * Render all Claude Code configuration files.
   */
  public static render(
    component: Component,
    rules: ReadonlyArray<AgentRule>,
    skills: ReadonlyArray<AgentSkill>,
    subAgents: ReadonlyArray<AgentSubAgent>,
    mcpServers: Readonly<Record<string, McpServerConfig>>,
    settings?: ClaudeSettingsConfig,
    procedures?: ReadonlyArray<AgentProcedure>,
    commands?: ReadonlyArray<AgentCommand>,
  ): void {
    ClaudeRenderer.renderClaudeMd(component, rules);
    ClaudeRenderer.renderScopedRules(component, rules);
    ClaudeRenderer.renderSettings(component, mcpServers, settings);
    ClaudeRenderer.renderSkills(component, skills);
    ClaudeRenderer.renderSubAgents(component, subAgents);
    if (procedures && procedures.length > 0) {
      ClaudeRenderer.renderProcedures(component, procedures);
    }
    if (commands && commands.length > 0) {
      ClaudeRenderer.renderCommands(component, commands);
    }
  }

  private static renderClaudeMd(
    component: Component,
    rules: ReadonlyArray<AgentRule>,
  ): void {
    const claudeMdRules = rules.filter((r) => {
      if (r.platforms?.claude?.exclude) return false;
      const target =
        r.platforms?.claude?.target ?? ClaudeRenderer.defaultTarget(r);
      return target === CLAUDE_RULE_TARGET.CLAUDE_MD;
    });

    if (claudeMdRules.length === 0) return;

    const lines: string[] = [GENERATED_MARKER, ""];
    for (let i = 0; i < claudeMdRules.length; i++) {
      if (i > 0) lines.push("", "---", "");
      lines.push(...claudeMdRules[i].content.split("\n"));
    }

    new TextFile(component, "CLAUDE.md", { lines });
  }

  private static renderScopedRules(
    component: Component,
    rules: ReadonlyArray<AgentRule>,
  ): void {
    const scopedRules = rules.filter((r) => {
      if (r.platforms?.claude?.exclude) return false;
      const target =
        r.platforms?.claude?.target ?? ClaudeRenderer.defaultTarget(r);
      return target === CLAUDE_RULE_TARGET.SCOPED_FILE;
    });

    for (const rule of scopedRules) {
      const lines: string[] = [];

      if (rule.filePatterns && rule.filePatterns.length > 0) {
        lines.push("---");
        lines.push("paths:");
        for (const pattern of rule.filePatterns) {
          lines.push(`  - "${pattern}"`);
        }
        lines.push("---");
        lines.push("");
      }

      lines.push(...rule.content.split("\n"));

      new TextFile(component, `.claude/rules/${rule.name}.md`, { lines });
    }
  }

  private static renderSettings(
    component: Component,
    mcpServers: Readonly<Record<string, McpServerConfig>>,
    settings?: ClaudeSettingsConfig,
  ): void {
    const obj: Record<string, unknown> = {};
    let hasContent = false;

    if (settings?.defaultMode) {
      obj.defaultMode = settings.defaultMode;
      hasContent = true;
    }

    if (settings?.permissions) {
      const perms: Record<string, unknown> = {};
      if (settings.permissions.allow?.length) {
        perms.allow = [...settings.permissions.allow];
      }
      if (settings.permissions.deny?.length) {
        perms.deny = [...settings.permissions.deny];
      }
      if (settings.permissions.ask?.length) {
        perms.ask = [...settings.permissions.ask];
      }
      if (settings.permissions.additionalDirectories?.length) {
        perms.additionalDirectories = [
          ...settings.permissions.additionalDirectories,
        ];
      }
      if (Object.keys(perms).length > 0) {
        obj.permissions = perms;
        hasContent = true;
      }
    }

    if (settings?.hooks) {
      const hooks: Record<string, unknown> = {};
      for (const [event, entries] of Object.entries(settings.hooks)) {
        if (entries && entries.length > 0) {
          hooks[event] = entries;
        }
      }
      if (Object.keys(hooks).length > 0) {
        obj.hooks = hooks;
        hasContent = true;
      }
    }

    // MCP servers from both cross-platform and Claude-specific config
    const allMcpServers: Record<string, unknown> = {};

    // Cross-platform MCP servers
    for (const [name, config] of Object.entries(mcpServers)) {
      allMcpServers[name] = ClaudeRenderer.buildMcpServerObj(config);
    }

    // Claude-specific MCP servers
    if (settings?.mcpServers) {
      for (const [name, config] of Object.entries(settings.mcpServers)) {
        allMcpServers[name] = ClaudeRenderer.buildMcpServerObj(config);
      }
    }

    if (Object.keys(allMcpServers).length > 0) {
      obj.mcpServers = allMcpServers;
      hasContent = true;
    }

    if (settings?.allowedMcpServers?.length) {
      obj.allowedMcpServers = [...settings.allowedMcpServers];
      hasContent = true;
    }

    if (settings?.deniedMcpServers?.length) {
      obj.deniedMcpServers = [...settings.deniedMcpServers];
      hasContent = true;
    }

    if (settings?.env && Object.keys(settings.env).length > 0) {
      obj.env = { ...settings.env };
      hasContent = true;
    }

    if (settings?.sandbox) {
      obj.sandbox = ClaudeRenderer.buildSandboxObj(settings.sandbox);
      hasContent = true;
    }

    if (settings?.autoMode) {
      const autoMode: Record<string, unknown> = {};
      if (settings.autoMode.environment?.length) {
        autoMode.environment = [...settings.autoMode.environment];
      }
      if (settings.autoMode.allow?.length) {
        autoMode.allow = [...settings.autoMode.allow];
      }
      if (settings.autoMode.soft_deny?.length) {
        autoMode.soft_deny = [...settings.autoMode.soft_deny];
      }
      if (Object.keys(autoMode).length > 0) {
        obj.autoMode = autoMode;
        hasContent = true;
      }
    }

    if (settings?.disableBypassPermissionsMode) {
      obj.disableBypassPermissionsMode = settings.disableBypassPermissionsMode;
      hasContent = true;
    }

    if (settings?.disableAutoMode) {
      obj.disableAutoMode = settings.disableAutoMode;
      hasContent = true;
    }

    if (settings?.disableAllHooks !== undefined) {
      obj.disableAllHooks = settings.disableAllHooks;
      hasContent = true;
    }

    if (settings?.excludeSensitivePatterns?.length) {
      obj.excludeSensitivePatterns = [...settings.excludeSensitivePatterns];
      hasContent = true;
    }

    if (settings?.model) {
      obj.model = settings.model;
      hasContent = true;
    }

    if (settings?.effortLevel) {
      obj.effortLevel = settings.effortLevel;
      hasContent = true;
    }

    if (settings?.attribution) {
      obj.attribution = settings.attribution;
      hasContent = true;
    }

    if (settings?.claudeMdExcludes?.length) {
      obj.claudeMdExcludes = [...settings.claudeMdExcludes];
      hasContent = true;
    }

    if (settings?.respectGitignore !== undefined) {
      obj.respectGitignore = settings.respectGitignore;
      hasContent = true;
    }

    if (!hasContent) return;

    new JsonFile(component, ".claude/settings.json", { obj });
  }

  private static buildSandboxObj(
    sandbox: NonNullable<ClaudeSettingsConfig["sandbox"]>,
  ): Record<string, unknown> {
    const obj: Record<string, unknown> = {};
    if (sandbox.enabled !== undefined) obj.enabled = sandbox.enabled;
    if (sandbox.mode) obj.mode = sandbox.mode;
    if (sandbox.failIfUnavailable !== undefined) {
      obj.failIfUnavailable = sandbox.failIfUnavailable;
    }
    if (sandbox.autoAllowBashIfSandboxed !== undefined) {
      obj.autoAllowBashIfSandboxed = sandbox.autoAllowBashIfSandboxed;
    }
    if (sandbox.excludedCommands?.length) {
      obj.excludedCommands = [...sandbox.excludedCommands];
    }
    if (sandbox.filesystem) {
      const fs: Record<string, unknown> = {};
      if (sandbox.filesystem.allowRead?.length) {
        fs.allowRead = [...sandbox.filesystem.allowRead];
      }
      if (sandbox.filesystem.denyRead?.length) {
        fs.denyRead = [...sandbox.filesystem.denyRead];
      }
      if (sandbox.filesystem.allowWrite?.length) {
        fs.allowWrite = [...sandbox.filesystem.allowWrite];
      }
      if (sandbox.filesystem.denyWrite?.length) {
        fs.denyWrite = [...sandbox.filesystem.denyWrite];
      }
      if (Object.keys(fs).length > 0) obj.filesystem = fs;
    }
    if (sandbox.network) {
      const net: Record<string, unknown> = {};
      if (sandbox.network.allowedDomains?.length) {
        net.allowedDomains = [...sandbox.network.allowedDomains];
      }
      if (sandbox.network.denyDomains?.length) {
        net.denyDomains = [...sandbox.network.denyDomains];
      }
      if (Object.keys(net).length > 0) obj.network = net;
    }
    return obj;
  }

  private static renderSkills(
    component: Component,
    skills: ReadonlyArray<AgentSkill>,
  ): void {
    for (const skill of skills) {
      if (skill.platforms?.claude?.exclude) continue;

      const lines: string[] = [];
      lines.push("---");
      lines.push(`name: "${skill.name}"`);
      lines.push(`description: "${skill.description}"`);
      if (skill.disableModelInvocation) {
        lines.push(`disable-model-invocation: true`);
      }
      if (skill.userInvocable === false) {
        lines.push(`user-invocable: false`);
      }
      const resolvedSkillModel = resolveModelAlias(skill.model);
      if (resolvedSkillModel) {
        lines.push(`model: "${resolvedSkillModel}"`);
      }
      if (skill.effort) {
        lines.push(`effort: "${skill.effort}"`);
      }
      if (skill.paths && skill.paths.length > 0) {
        lines.push(`paths:`);
        for (const p of skill.paths) {
          lines.push(`  - "${p}"`);
        }
      }
      if (skill.context) {
        lines.push(`context: "${skill.context}"`);
      }
      if (skill.agent) {
        lines.push(`agent: "${skill.agent}"`);
      }
      if (skill.shell) {
        lines.push(`shell: "${skill.shell}"`);
      }
      if (skill.allowedTools && skill.allowedTools.length > 0) {
        lines.push(`allowed-tools:`);
        for (const tool of skill.allowedTools) {
          lines.push(`  - "${tool}"`);
        }
      }
      lines.push("---");
      lines.push("");
      lines.push(...skill.instructions.split("\n"));

      new TextFile(component, `.claude/skills/${skill.name}/SKILL.md`, {
        lines,
      });

      if (skill.referenceFiles && skill.referenceFiles.length > 0) {
        for (const file of skill.referenceFiles) {
          new TextFile(component, `.claude/skills/${skill.name}/${file.path}`, {
            lines: file.content.split("\n"),
          });
        }
      }
    }
  }

  private static renderSubAgents(
    component: Component,
    subAgents: ReadonlyArray<AgentSubAgent>,
  ): void {
    for (const agent of subAgents) {
      if (agent.platforms?.claude?.exclude) continue;

      const lines: string[] = [];
      lines.push("---");
      lines.push(`name: ${agent.name}`);
      lines.push(`description: >-`);
      lines.push(`  ${agent.description}`);
      const resolvedModel = resolveModelAlias(agent.model);
      if (resolvedModel) {
        lines.push(`model: ${resolvedModel}`);
      }
      if (agent.tools && agent.tools.length > 0) {
        lines.push(`tools:`);
        for (const tool of agent.tools) {
          lines.push(`  - "${tool}"`);
        }
      }
      if (agent.disallowedTools && agent.disallowedTools.length > 0) {
        lines.push(`disallowedTools:`);
        for (const tool of agent.disallowedTools) {
          lines.push(`  - "${tool}"`);
        }
      }
      if (agent.maxTurns) {
        lines.push(`maxTurns: ${agent.maxTurns}`);
      }
      if (agent.skills && agent.skills.length > 0) {
        lines.push(`skills:`);
        for (const skill of agent.skills) {
          lines.push(`  - "${skill}"`);
        }
      }
      if (agent.platforms?.claude?.permissionMode) {
        lines.push(`permissionMode: ${agent.platforms.claude.permissionMode}`);
      }
      if (agent.platforms?.claude?.isolation) {
        lines.push(`isolation: ${agent.platforms.claude.isolation}`);
      }
      if (agent.platforms?.claude?.background) {
        lines.push(`background: true`);
      }
      if (agent.platforms?.claude?.effort) {
        lines.push(`effort: ${agent.platforms.claude.effort}`);
      }
      if (agent.platforms?.claude?.memory) {
        lines.push(`memory: ${agent.platforms.claude.memory}`);
      }
      lines.push("---");
      lines.push("");
      lines.push(...agent.prompt.split("\n"));

      new TextFile(component, `.claude/agents/${agent.name}.md`, { lines });
    }
  }

  private static buildMcpServerObj(
    config: McpServerConfig,
  ): Record<string, unknown> {
    const server: Record<string, unknown> = {};
    if (config.transport) server.type = config.transport;
    if (config.command) server.command = config.command;
    if (config.args) server.args = [...config.args];
    if (config.url) server.url = config.url;
    if (config.headers && Object.keys(config.headers).length > 0) {
      server.headers = { ...config.headers };
    }
    if (config.env) server.env = { ...config.env };
    return server;
  }

  private static renderProcedures(
    component: Component,
    procedures: ReadonlyArray<AgentProcedure>,
  ): void {
    for (const proc of procedures) {
      new TextFile(component, `.claude/procedures/${proc.name}`, {
        lines: proc.content.split("\n"),
        executable: true,
      });
    }
  }

  /**
   * Renders user-invokable slash commands to `.claude/commands/<name>.md`.
   *
   * Each command file carries a YAML frontmatter block with the
   * `description` (always present) and an optional `model` alias. The
   * body is the {@link AgentCommand.content} written verbatim.
   *
   * @see https://docs.claude.com/en/docs/claude-code/slash-commands
   */
  private static renderCommands(
    component: Component,
    commands: ReadonlyArray<AgentCommand>,
  ): void {
    for (const command of commands) {
      const lines: string[] = [];
      lines.push("---");
      lines.push(`description: ${command.description}`);
      const resolvedModel = resolveModelAlias(command.model);
      if (resolvedModel) {
        lines.push(`model: ${resolvedModel}`);
      }
      lines.push("---");
      lines.push("");
      lines.push(...command.content.split("\n"));

      new TextFile(component, `.claude/commands/${command.name}.md`, { lines });
    }
  }

  /**
   * Determine the default Claude rule target based on rule scope.
   * ALWAYS-scoped rules default to CLAUDE_MD; FILE_PATTERN rules default to SCOPED_FILE.
   */
  private static defaultTarget(rule: AgentRule): string {
    return rule.scope === AGENT_RULE_SCOPE.ALWAYS
      ? CLAUDE_RULE_TARGET.CLAUDE_MD
      : CLAUDE_RULE_TARGET.SCOPED_FILE;
  }
}
